import type { Order } from "@@/shared/orders/order";
import { randomInt } from "@@/utilities/random-int";

type generatorType = "RANDOM" | "ADDRESS" | "SHORT_ADDRESS" | "SLUG";

const packageIdentiferGenerator = (
    order: Order,
    type: generatorType,
    shipments: number,
) => {
    if (order.delivery.deliveryMethodType === "HOME_DELIVERY") {
        const zoneIdentifier =
            order.delivery.destination.deliveryZone.shortIdentifier;

        switch (type) {
            case "RANDOM": {
                const code = randomInt({
                    min: 100,
                    max: 999,
                });
                const street = getShortStreetID(
                    order.delivery.destination.address.addressRows,
                );
                return [zoneIdentifier, code.toString(), street]
                    .join("-")
                    .toUpperCase();
            }

            case "ADDRESS": {
                return [
                    zoneIdentifier,
                    order.delivery.destination.address.addressRows[0],
                ]
                    .join("-")
                    .replace(" ", "-")
                    .toUpperCase();
            }
            case "SHORT_ADDRESS": {
                const shipmentCountSuffix =
                    shipments > 0 ? "#" + shipments : "";
                const shorted = getAllAddressParts(
                    order.delivery.destination.address.addressRows,
                ).map(s => shortenAddress(s));

                return [zoneIdentifier, ...shorted, shipmentCountSuffix]
                    .filter(s => s !== "")
                    .join("-")
                    .replace(" ", "-")
                    .toUpperCase();
            }
            case "SLUG": {
                const street = getShortStreetID(
                    order.delivery.destination.address.addressRows,
                );

                return [zoneIdentifier, order.slug, street]
                    .join("-")
                    .toUpperCase();
            }
        }
    } else {
        throw new Error("NOT SUPPORTED");
    }
};

const getAllAddressParts = (rows: string[]) => {
    const shorted = rows.flatMap(a => {
        return a.split(" ");
    });
    return shorted;
};
const getShortStreetID = (rows1: string[]) => {
    const rows = getAllAddressParts(rows1);
    const streetNumber = rows.find(part => part.match(/\d+/g)) ?? null;
    if (streetNumber) return rows[0][0] + streetNumber;
    else return rows[0].slice(0, 3);
};

const shortenAddress = (part: string) => {
    const containsNumber = part.match(/\d+/g);
    if (containsNumber != null) {
        return part;
    }

    const result =
        addressWords
            .map(f => {
                const d = f.matches.filter(m => part.includes(m));
                if (d.length > 0) {
                    const match = d[d.length - 1];
                    const index = part.indexOf(match);
                    const firstPart = part.substring(0, index).slice(0, 4);

                    const lastPart = part.substring(index);

                    return (
                        firstPart +
                        lastPart.replace(d[d.length - 1], f.replacement)
                    );
                }
                return undefined;
            })
            .find(f => f) ?? part.slice(0, 4);

    return result;
};

type words = {
    matches: string[];
    replacement: string;
};

const addressWords: words[] = [
    { matches: ["gata", "gatan"], replacement: "ga" },

    { matches: ["väg", "vägen"], replacement: "vä" },
    { matches: ["park", "parken"], replacement: "pk" },
];

// bostad = bst. alue = al.
// brinken = br. asunto = as.
// bygata(n) = bg. aukio = auk.
// gata(n) = g. kaari = kri
// gränd(en) = gr. katu = k.
// led(en) = l. kerros = krs
// park(en) = pk. kuja = kj.
// skvär(et) = skv. kylä = kl.
// stig(en) = st. penger = pgr
// strand(en) = str. polku = p.
// stråk(et) = sk. puistikko = pko
// sväng(en) = sv. puisto = ps.
// torg(et) = tg. raitti = r.
// väg(en) = v. ranta = rt.
// våning = vån.

export { packageIdentiferGenerator };
